import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Card,
  Button,
  Space,
  message,
  Popconfirm,
  Alert,
  Tag,
  Typography,
} from 'antd';
import {
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons';
import apiService from '../../services/api';
import { formatDate } from '../../utils/helpers';
import { formatCurrency } from '../../utils/formatters';
import dayjs from 'dayjs';
import EmptyState from '../../components/EmptyState';
import PaymentStatsCards from './PaymentStatsCards';
import PaymentFilters from './PaymentFilters';

const PaymentList = () => {
  const navigate = useNavigate();
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    income: 0,
    expense: 0,
    incomeVAT: 0,
    expenseVAT: 0,
  });
  const [dateFilter, setDateFilter] = useState({
    year: dayjs().year(),
    month: dayjs().month(),
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filteredTotal, setFilteredTotal] = useState(0);
  const [filters, setFilters] = useState({
    status: [],
    dateRange: null,
    plPosition: '',
    category: '',
    channel: '',
    van: '',
    search: '',
  });
  const [channels, setChannels] = useState([]);
  const [vans, setVans] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [channelsRes, vansRes] = await Promise.all([
          apiService.channels.getAll(),
          apiService.vans.getAll()
        ]);
        setChannels(channelsRes.data || []);
        setVans(vansRes.data || []);
      } catch (err) {
        console.error('Failed to fetch data', err);
      }
    };
    fetchData();
  }, []);

  const handleDateFilterChange = (newValue) => {
    setDateFilter(prev => {
      if (
        prev.year === newValue.year &&
        prev.month === newValue.month &&
        !newValue.dateRange
      ) return prev;
      return newValue;
    });
    setCurrentPage(1);
  };

  const fetchPayments = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        status: filters.status.length > 0 ? filters.status : undefined,
        ...(dateFilter.month === 'custom'
          ? {
              startDate: dateFilter.dateRange[0]?.format('YYYY-MM-DD'),
              endDate: dateFilter.dateRange[1]?.format('YYYY-MM-DD'),
            }
          : {
              year: dateFilter.year,
              ...(dateFilter.month !== undefined && { month: dateFilter.month }),
            }
        ),
        plPosition: filters.plPosition,
        category: filters.category,
        channel: filters.channel,
        van: filters.van,
        search: filters.search,
        page: currentPage,
        limit: pageSize,
      };
      const response = await apiService.payments.getAll(params);
      const data = response?.data || {};

      setPayments(data.data || []);
      setFilteredTotal(data.filteredTotal || 0);
      setStats({
        income: data.filteredTotalAmount || 0,
        expense: data.filteredTotalExpense || 0,
        incomeVAT: data.filteredTotalIncomeVAT || 0,
        expenseVAT: data.filteredTotalExpenseVAT || 0,
      });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load payments. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [filters, dateFilter, currentPage, pageSize]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  const handleDelete = useCallback(async (id) => {
    try {
      await apiService.payments.delete(id);
      message.success('Payment deleted successfully');
      fetchPayments();
    } catch (error) {
      console.error('Error deleting payment:', error);
      message.error('Failed to delete payment');
    }
  }, [fetchPayments]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid':
        return 'success';
      case 'pending':
        return 'warning';
      case 'overdue':
        return 'error';
      case 'cancelled':
        return 'default';
      default:
        return 'default';
    }
  };

  const columns = useMemo(() => [
    {
      title: 'Reference',
      dataIndex: 'reference',
      key: 'reference',
      render: (text, record) => (
        <Typography.Link
          href={`/payments/${record._id}`}
          onClick={e => { if (e.ctrlKey || e.metaKey || e.button === 1) return; e.preventDefault(); navigate(`/payments/${record._id}`); }}
        >
          {text}
        </Typography.Link>
      ),
    },
    {
      title: 'Amount',
      dataIndex: 'totalAmount',
      key: 'totalAmount',
      render: (amount) => formatCurrency(amount),
    },
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category) => (
        <Tag color="blue" style={{ textTransform: 'capitalize' }}>
          {category}
        </Tag>
      ),
      filters: [
        { text: 'Fuel', value: 'fuel' },
        { text: 'Maintenance', value: 'maintenance' },
        { text: 'Insurance', value: 'insurance' },
        { text: 'Insurance Claim', value: 'insurance claim' },
        { text: 'Salary', value: 'salary' },
        { text: 'Van Rental', value: 'van rental' },
        { text: 'Van Deposits', value: 'van deposits' },
        { text: 'Tracking Charges', value: 'tracking charges' },
        { text: 'Office', value: 'office' },
        { text: 'Job', value: 'job' },
        { text: 'Charges', value: 'charges' },
        { text: 'Porter', value: 'porter' },
        { text: 'Accounting Services', value: 'accounting services' },
        { text: 'PAYE', value: 'paye' },
        { text: 'Excess Mileage', value: 'excess mileage' },
        { text: 'VAT Liability', value: 'vat liability' },
        { text: 'Other', value: 'other' },
      ],
      onFilter: (value, record) => record.category === value,
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      render: (date) => formatDate(date, 'DD/MM/YYYY', true),
      sorter: (a, b) => dayjs(a.dueDate).unix() - dayjs(b.dueDate).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={getStatusColor(status)}>
          {status?.toUpperCase() || 'N/A'}
        </Tag>
      ),
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date) => formatDate(date),
      sorter: (a, b) => dayjs(a.createdAt).unix() - dayjs(b.createdAt).unix(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'PL Position',
      dataIndex: 'plPosition',
      key: 'plPosition',
      render: (plPosition) => (
        <Tag color={plPosition === 'income' ? 'green' : 'volcano'} style={{ textTransform: 'capitalize' }}>
          {plPosition}
        </Tag>
      ),
      filters: [
        { text: 'Income', value: 'income' },
        { text: 'Expense', value: 'expense' },
      ],
      onFilter: (value, record) => record.plPosition === value,
    },
    {
      title: 'Van',
      dataIndex: 'relatedVan',
      key: 'van',
      render: (van) => van?.registration || 'N/A',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/payments/edit/${record._id}`)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Are you sure you want to delete this payment?"
            onConfirm={() => handleDelete(record._id)}
            okText="Yes"
            cancelText="No"
            okButtonProps={{ danger: true }}
          >
            <Button danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ], [navigate, handleDelete]);

  return (
    <div>
      {error && (
        <Alert
          message="Failed to load payments"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          action={<Button size="small" onClick={fetchPayments}>Retry</Button>}
        />
      )}

      <PaymentStatsCards loading={loading} stats={stats} />

      <Card style={{ marginTop: 16 }}>
        <PaymentFilters
          filters={filters}
          setFilters={setFilters}
          dateFilter={dateFilter}
          onDateFilterChange={handleDateFilterChange}
          vans={vans}
          channels={channels}
          onFetch={fetchPayments}
          onCreatePayment={() => navigate('/payments/create')}
        />
        <Table
          columns={columns}
          dataSource={payments}
          rowKey="_id"
          loading={loading}
          style={{ marginTop: 16 }}
          locale={{ emptyText: <EmptyState message="No payments found" hint="Try adjusting your filters or date range" /> }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredTotal,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => { setCurrentPage(page); setPageSize(size); },
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} payments`,
          }}
        />
      </Card>
    </div>
  );
};

export default PaymentList; 